import React from "react"
import { useDispatch, useSelector } from "react-redux";
import { StyledModalBody } from "../../../../../../components/Modals";
import { ButtonBodyVariant } from "../../../../../../components/ui/ButtonVariant";
import { useTranslation } from "react-i18next";
import { closeActiveModal, openModal } from "../../../../../../helper/modals";
import { Flex } from "../../../../../../components/ui/Flex";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../../../../components/ui/Form";
import { useForm, useWatch } from "react-hook-form";
import { CustomDatePicker } from "../../../../../../components/CustomSearchBar";
import { DATE_FORMATS } from "../../../../../users/constant";
import { ModalButtonsContainer, PaymentModal, StepItem, LightBlueText, TitleText } from "../../../../PaymentComponents";
import * as Yup from 'yup';
import { yupResolver } from "@hookform/resolvers/yup";
import { updatePaymentData } from "../../../../../../redux/actions/payments";
import moment from "moment";

const schema = Yup.object().shape({
  startDate: Yup.mixed()
    .required('A contract start date is required'),
  endDate: Yup.mixed()
    .required('A contract end date is required'),
});

const CreateRecurringPaymentStep1 = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const modal = useSelector((state) => state.headerModal.modal);
  const addPayment = useSelector((state) => state.addPayment);

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      startDate: addPayment.startDate ? moment(addPayment.startDate) : null,
      endDate: addPayment.endDate ? moment(addPayment.endDate) : null,
    }
  });

  const startDate = useWatch({
    control: form.control,
    name: 'startDate'
  });

  const endDate = useWatch({
    control: form.control,
    name: 'endDate'
  });

  const disabledEndDates = (current) => {
    if (!current) return false;

    const nowUtc = moment.utc().startOf('day');

    if (!startDate) {
      return moment(current).utc().startOf('day').isBefore(nowUtc);
    }

    return moment(current).utc().startOf('day').isBefore(
      moment(startDate).utc().startOf('day').add(1, 'day')
    );
  };

  const disabledStartDates = (current) => {
    if (!current) return false;

    const nowUtc = moment.utc().startOf('day');
    const currentUtc = moment(current).utc().startOf('day');
    const isBeforeOrToday = currentUtc.isSameOrBefore(nowUtc);
    const isAfterEndDate = endDate && currentUtc.isAfter(moment(endDate).utc().startOf('day'));

    return isBeforeOrToday || isAfterEndDate;
  };

  const onSubmit = (values) => {
    dispatch(updatePaymentData({
      startDate: values.startDate ? moment(values.startDate).utc().format(DATE_FORMATS.YYYY_MM_DD) : null,
      endDate: values.endDate ? moment(values.endDate).utc().format(DATE_FORMATS.YYYY_MM_DD) : null,
    }))
    openModal('CreateRecurringPaymentStep2')
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <PaymentModal
          show={modal === 'CreateRecurringPaymentStep1'}
          onClose={closeActiveModal}
          closeOnBackdrop={false}
          width={'460px'}>
          <StyledModalBody>
            <Flex direction="column" gap="1rem">
              <Flex direction="column">
                <Flex justify="space-between" alignItems="center" style={{ width: "100%" }}>
                  <LightBlueText> STEP 1/3</LightBlueText>
                  <Flex gap="0.5rem">
                    <StepItem filled />
                    <StepItem />
                    <StepItem />
                  </Flex>
                </Flex>
                <TitleText>Payment Dates</TitleText>
              </Flex>

              <Flex direction="column" alignItems="center" gap="1rem">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem alt>
                      <FormLabel v3>{t('Start Date')}</FormLabel>
                      <FormControl>
                        <CustomDatePicker
                          {...field}
                          onOpenChange={(open) => {
                            if (open) {
                              form.setValue('endDate', null, { shouldValidate: false, shouldDirty: true });
                              form.clearErrors('endDate');
                            }
                          }}
                          className="form-control"
                          width={'100%'}
                          height={'3rem'}
                          showSecond={false}
                          format={DATE_FORMATS.MMMM_DD_YYYY}
                          placeholder={t('Select date')}
                          disabledDate={disabledStartDates}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem alt>
                      <FormLabel v3>{t('End Date')}</FormLabel>
                      <FormControl>
                        <CustomDatePicker
                          {...field}
                          className="form-control"
                          width={'100%'}
                          height={'3rem'}
                          showSecond={false}
                          format={DATE_FORMATS.MMMM_DD_YYYY}
                          placeholder={t('Select date')}
                          disabledDate={disabledEndDates}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

              </Flex>
              <ModalButtonsContainer gapY="8px">
                <ButtonBodyVariant cssOverride="width: 8.875rem; height: 2rem;" type="button" variant="secondary" onClick={closeActiveModal}>{t('Cancel')}</ButtonBodyVariant>
                <ButtonBodyVariant cssOverride="width: 8.875rem; height: 2rem;" type="submit">{t('Next')}</ButtonBodyVariant>
              </ModalButtonsContainer>
            </Flex>
          </StyledModalBody>
        </PaymentModal>
      </form>
    </Form>
  )
}

export default CreateRecurringPaymentStep1
